import type { AppNavigationTarget } from "@/lib/navigation/appNavigation";
import { buildDocumentLinkTargetFields } from "@/lib/navigation/appNavigation";
import {
  DocumentIndexType,
  normalizeDocumentIndex,
} from "@/models/conversation/document";

const LEGISLATION_HTML_PATH =
  /^\/(?:chat\/)?legislation\/([^/]+)\/datasource\/([^/]+)\/?$/i;
const LEGISLATION_PDF_PATH =
  /^\/(?:chat\/)?legislation\/([^/]+)\/datasource\/([^/]+)\/pdf\/?$/i;
const CHAT_LEGISLATION_PDF_PATH = /^\/chat\/legislation\/([^/]+)\/?$/i;
const WORKSPACE_HTML_PATH =
  /^\/workspaces\/([^/]+)\/document\/([^/]+)\/html\/?$/i;
const WORKSPACE_PDF_PATH =
  /^\/workspaces\/([^/]+)\/document\/([^/]+)\/type\/[^/]+\/?$/i;
const CHAT_WORKSPACE_HTML_PATH =
  /^\/chat\/workspace\/([^/]+)\/document\/([^/]+)\/?$/i;
const CHAT_WORKSPACE_PDF_PATH =
  /^\/chat\/workspace\/([^/]+)\/document\/([^/]+)\/excel\/[^/]+\/?$/i;
const WORKSPACE_REDIRECT_PATH =
  /^\/workspaces\/([^/]+)\/document\/([^/]+)\/?$/i;

export const isDocumentViewerPath = (pathname: string): boolean =>
  LEGISLATION_HTML_PATH.test(pathname) ||
  LEGISLATION_PDF_PATH.test(pathname) ||
  CHAT_LEGISLATION_PDF_PATH.test(pathname) ||
  WORKSPACE_HTML_PATH.test(pathname) ||
  WORKSPACE_PDF_PATH.test(pathname) ||
  CHAT_WORKSPACE_HTML_PATH.test(pathname) ||
  CHAT_WORKSPACE_PDF_PATH.test(pathname) ||
  WORKSPACE_REDIRECT_PATH.test(pathname);

const legislationKey = (
  documentId: string,
  source: string | undefined,
  isChat: boolean
): string => {
  const prefix = isChat ? "chat-legislation" : "legislation";
  return source
    ? `${prefix}:${documentId}:${source.toLowerCase()}`
    : `${prefix}:${documentId}`;
};

const workspaceKey = (
  workspaceId: string,
  documentId: string,
  isChat: boolean
): string => {
  const prefix = isChat ? "chat-workspace" : "workspace";
  return `${prefix}:${workspaceId}:${documentId}`;
};

export const getDocumentKeyFromPath = (pathname: string): string | null => {
  const isChat = pathname.startsWith("/chat/");

  let match = LEGISLATION_HTML_PATH.exec(pathname);
  if (match) {
    return legislationKey(match[1], match[2], isChat);
  }

  match = LEGISLATION_PDF_PATH.exec(pathname);
  if (match) {
    return legislationKey(match[1], match[2], isChat);
  }

  match = CHAT_LEGISLATION_PDF_PATH.exec(pathname);
  if (match) {
    return legislationKey(match[1], undefined, true);
  }

  match = WORKSPACE_HTML_PATH.exec(pathname);
  if (match) {
    return workspaceKey(match[1], match[2], false);
  }

  match = WORKSPACE_PDF_PATH.exec(pathname);
  if (match) {
    return workspaceKey(match[1], match[2], false);
  }

  match = WORKSPACE_REDIRECT_PATH.exec(pathname);
  if (match) {
    return workspaceKey(match[1], match[2], false);
  }

  match = CHAT_WORKSPACE_HTML_PATH.exec(pathname);
  if (match) {
    return workspaceKey(match[1], match[2], true);
  }

  match = CHAT_WORKSPACE_PDF_PATH.exec(pathname);
  if (match) {
    return workspaceKey(match[1], match[2], true);
  }

  return null;
};

export const getDocumentKeyFromTarget = (
  target: AppNavigationTarget,
  pathname?: string
): string | null => {
  switch (target.kind) {
    case "legislation":
      return legislationKey(
        target.documentId,
        target.source,
        target.context === "chat" ||
          (target.context === undefined && pathname?.includes("/chat/") === true)
      );

    case "legislationPdf":
      return legislationKey(
        target.documentId,
        target.source,
        target.context === "chat" ||
          (target.context === undefined && pathname?.includes("/chat/") === true)
      );

    case "workspaceDocument":
      return workspaceKey(
        target.workspaceId,
        target.documentId,
        pathname?.includes("/chat/") === true
      );

    case "documentLink": {
      const resolved = buildDocumentLinkTargetFields(
        target.link,
        target.currentDocumentId,
        target.context
      );
      return resolved ? getDocumentKeyFromTarget(resolved, pathname) : null;
    }

    case "documentReference": {
      const documentIndex = normalizeDocumentIndex(
        target.reference.documentIndex ?? ""
      );
      if (documentIndex === DocumentIndexType.Retsinformation) {
        return legislationKey(
          target.reference.documentId,
          target.reference.source,
          true
        );
      }
      if (
        documentIndex === DocumentIndexType.Workspace &&
        target.reference.workspaceId
      ) {
        return workspaceKey(
          target.reference.workspaceId,
          target.reference.documentId,
          true
        );
      }
      return null;
    }

    case "path": {
      const pathOnly = target.path.split("?")[0] ?? target.path;
      return getDocumentKeyFromPath(pathOnly);
    }

    default:
      return null;
  }
};

export const isDocumentViewerNavigationTarget = (
  target: AppNavigationTarget
): boolean => {
  switch (target.kind) {
    case "legislation":
    case "legislationPdf":
    case "workspaceDocument":
    case "documentLink":
    case "documentReference":
      return getDocumentKeyFromTarget(target) !== null;
    case "path":
      return isDocumentViewerPath(target.path.split("?")[0] ?? target.path);
    default:
      return false;
  }
};
